import {Observer} from "mobx-react";
import type {CSSProperties, MouseEvent as ReactMouseEvent, RefObject} from "react";
import {useEffect, useReducer, useRef, useState} from "react";
import styled from "styled-components";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {IModel} from "../../../../../data/models/Model";
import type {Catalog} from "../../../../../data/models/Catalog";
import type {ICreateUnplottedXyiconParam} from "../../../../modules/abstract/ModuleView";
import {ReactUtils} from "../../../../utils/ReactUtils";
import {colorPalette} from "../../../styles/colorPalette";
import {baseDistance, FlexCenterStyle, fontSize, fontWeight, radius, zIndex} from "../../../styles/styles";
import DockIcon from "../../../icons/dock.svg?react";
import UndockIcon from "../../../icons/undock.svg?react";
import CloseIcon from "../../../icons/xmark.svg?react";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import type {GridViewV5} from "../../../abstract/GridViewV5";
import {useClickOutside, useCloseOnKeyboardPress} from "../../../utils";
import {HTMLUtils} from "../../../../../utils/HTML/HTMLUtils";
import {PanelStyled} from "../../../abstract/Panel";
import {ToolButtonStyled} from "../toolbar/ToolButtonV5";
import {ShadowDivStyled} from "../../../details/ShadowDiv";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import {Constants} from "../../../../modules/space/spaceeditor/logic3d/Constants";
import {BoundaryTypeContainerV5} from "./BoundaryTypeContainerV5";
import {XyiconCatalogContainerV5} from "./XyiconCatalogContainerV5";

export type DockableTitle = "Catalog" | "Boundary" | "Unplotted Xyicons";

interface IDockableProps {
	readonly style?: CSSProperties;
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly gridView?: RefObject<GridViewV5<IModel>>;
	readonly setDocked: (value: boolean, title: DockableTitle) => void;
	readonly setOpen: (value: boolean, title: DockableTitle) => void;
	readonly isDocked: boolean;
	readonly title: DockableTitle;
	readonly setActiveTool: (id: string) => void;
	readonly onAddCatalogClick: (event: ReactMouseEvent) => void;
	readonly onDuplicateCatalogClick: (catalog: Catalog) => void;
	readonly onCreateUnplottedXyicons: (params: ICreateUnplottedXyiconParam[]) => Promise<void> | void;
}

const _animationTime: number = 300;

/**
 * When the panel is docked, SplitterV5 reads min/default/max width from this root. Use the same values for every
 * docked panel type so saved column widths do not jump when you switch between Catalog, Boundary, and Unplotted.
 */
const DOCKED_SPLITTER_PANE_MIN_WIDTH_PX = 186;
const DOCKED_SPLITTER_PANE_DEFAULT_WIDTH_PX = 440;
const DOCKED_SPLITTER_PANE_MAX_WIDTH_PX = 800;

export const DockableV5 = (props: IDockableProps) => {
	const {isDocked, title, setDocked, setOpen, spaceViewRenderer, setActiveTool, style} = props;
	const _previousTitleRef = useRef<DockableTitle>(null);
	const _elementRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	useCloseOnKeyboardPress(
		() => {
			if (isDocked) {
				return;
			}

			onClose();
			setTimeout(() => {
				setOpen(false, title);
			}, _animationTime);
		},
		KeyboardListener.KEY_ESCAPE,
		[isDocked],
	);

	const onDockTogglerClick = () => {
		setDocked(!isDocked, title);
	};

	const onClose = () => {
		setIsOpen(false);
	};

	const onCloseClick = () => {
		onClose();
		if (isDocked) {
			setOpen(false, title);
		} else {
			setTimeout(() => {
				setOpen(false, title);
			}, _animationTime);
		}
	};

	const onClickOutside = (event: MouseEvent) => {
		if (event.button !== Constants.MOUSE_BUTTON.LEFT) {
			return;
		}

		if (!isDocked) {
			onCloseClick();
		}
	};

	const onBoundaryTypeClick = (typeId: string) => {
		spaceViewRenderer.boundaryManager.setTypeId(typeId);
		setActiveTool("boundary");

		if (!isDocked) {
			onCloseClick();
		}
	};

	const fadeIn = (milliseconds: number) => {
		setTimeout(() => {
			forceUpdate();
		}, milliseconds);
	};

	const getElement = (renderTitle: DockableTitle) => {
		const {gridView, onAddCatalogClick, onDuplicateCatalogClick, onCreateUnplottedXyicons} = props;

		if (renderTitle === "Boundary") {
			return (
				<BoundaryTypeContainerV5
					spaceViewRenderer={spaceViewRenderer}
					onBoundaryTypeClick={onBoundaryTypeClick}
				/>
			);
		} else {
			return (
				<XyiconCatalogContainerV5
					spaceViewRenderer={spaceViewRenderer}
					gridView={gridView}
					renderTitle={renderTitle}
					onAddCatalogClick={onAddCatalogClick}
					onDuplicateCatalogClick={onDuplicateCatalogClick}
					onCreateUnplottedXyicons={onCreateUnplottedXyicons}
					isDocked={isDocked}
					isOpen={isOpen}
					onClose={onClose}
					onCloseClick={onCloseClick}
				/>
			);
		}
	};

	useEffect(() => {
		_previousTitleRef.current = title;
	});

	useClickOutside([_elementRef], onClickOutside, "mousedown", [
		`.${HTMLUtils.popupBackdropV5Class}`,
		`#${HTMLUtils.modalContainerId}`,
		`${ShadowDivStyled}`,
		`${ShadowDivStyled} + ${PanelStyled}`,
		`${ToolButtonStyled}[data-title="Draw Boundary"]`,
		`${ToolButtonStyled}[data-title="Catalog Panel"]`,
		`${ToolButtonStyled}[data-title="Unplotted Xyicons"]`,
	]);

	const dockTitle = isDocked ? `Undock ${title}` : `Dock ${title}`;

	if (_previousTitleRef.current == null) {
		_previousTitleRef.current = title;
		requestAnimationFrame(() => {
			setIsOpen(true);
		});
	}

	const hasChanged = _previousTitleRef.current !== title;

	if (hasChanged) {
		fadeIn(_previousTitleRef.current === null ? 0 : _animationTime);
	}

	const renderTitle = _previousTitleRef.current || title;
	const isVisible = isOpen && title === _previousTitleRef.current;
	const isCatalogPanel = renderTitle != "Boundary";
	const minWidth = isDocked ? DOCKED_SPLITTER_PANE_MIN_WIDTH_PX : isCatalogPanel ? 186 : 400;
	const defaultWidth = DOCKED_SPLITTER_PANE_DEFAULT_WIDTH_PX;
	const maxWidth = isDocked ? DOCKED_SPLITTER_PANE_MAX_WIDTH_PX : isCatalogPanel ? 800 : 0;

	return (
		<Observer>
			{() => {
				return (
					<DockableStyled
						ref={_elementRef}
						style={style ?? {}}
						className={ReactUtils.cls("Dockable", {docked: isDocked, visible: isVisible})}
						data-min-width={minWidth}
						data-default-width={defaultWidth}
						data-max-width={maxWidth}
						$isCatalogPanel={isCatalogPanel}
					>
						<Header data-cy={`${renderTitle.toLowerCase()}-title`}>
							<Title className="title">{renderTitle}</Title>
							<ButtonContainer>
								<IconButtonV5
									onClick={onDockTogglerClick}
									onMouseDown={(e) => {
										e.stopPropagation();
									}}
									title={dockTitle}
									IconComponent={isDocked ? UndockIcon : DockIcon}
								/>
								<IconButtonV5
									onClick={onCloseClick}
									onMouseDown={(e) => {
										e.stopPropagation();
									}}
									IconComponent={CloseIcon}
								/>
							</ButtonContainer>
						</Header>
						{getElement(renderTitle)}
					</DockableStyled>
				);
			}}
		</Observer>
	);
};

const DockableStyled = styled.div<{$isCatalogPanel: boolean}>`
	position: absolute;
	top: 50%;
	left: 75px;
	min-width: 186px;
	width: 336px;
	display: flex;
	flex-direction: column;
	flex: 1;
	opacity: 0;
	z-index: ${zIndex.moduleFloatingDockable};
	min-width: 0;
	background-color: ${colorPalette.white};
	box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.25);
	pointer-events: none;
	transform: translateY(-50%);
	transition: ease-in-out all 0.3s;

	&:not(.docked) {
		max-height: ${(props) => props.$isCatalogPanel && "calc(100% - 200px)"};
		border-radius: ${radius.md};
	}

	&.visible {
		pointer-events: initial;
		opacity: 1;
	}

	&.docked {
		width: initial;
		position: relative;
		left: 0;
		top: 0;
		flex: 1;
		transform: initial;
		box-shadow: none;
		min-height: 0;
		z-index: 11;
	}
`;

const Header = styled.div`
	${FlexCenterStyle};
	justify-content: space-between;
	gap: ${baseDistance.sm};
	min-width: 0;
	padding: ${baseDistance.md};
	flex-wrap: nowrap;
	align-items: center;
	position: relative;
	z-index: 2;
`;

const Title = styled.div`
	flex: 1;
	min-width: 0;
	font-size: ${fontSize.xl};
	line-height: 24px;
	font-weight: ${fontWeight.bold};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const ButtonContainer = styled.div`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	gap: 5px;
	position: relative;
	z-index: 2;
`;
